/* Warenkorb-Ergänzungen für Expanse. Keine Bestelldaten und keine zusätzlichen Tracker. */
(() => {
  if (customElements.get('bunaroba-cart-state')) return;
  const stateEvent = 'bunaroba:cart-state';
  let currentState;
  const requests = new Map();
  const root = () => window.Shopify?.routes?.root || '/';
  const locale = () => ({ de: 'de-CH', en: 'en-CH', fr: 'fr-CH', it: 'it-CH' })[document.documentElement.lang]
    || document.documentElement.lang || 'de-CH';

  function recommendation(productId, intent) {
    const key = `${root()}${productId}:${intent}`;
    if (!requests.has(key)) {
      const request = fetch(`${root()}recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`, {
        signal: AbortSignal.timeout(8000)
      }).then(response => {
        if (!response.ok) throw new Error('Empfehlungen nicht verfügbar');
        return response.json();
      }).then(data => data.products || []).catch(() => {
        requests.delete(key);
        return [];
      });
      requests.set(key, request);
    }
    return requests.get(key);
  }

  function eligible(state) {
    return state?.count > 0 && state.requiresShipping;
  }

  // Geschenk-Zustand aus der Versandanzeige. Expanse rendert sie zusammen mit dem Warenkorbzustand.
  function gift() {
    const data = document.querySelector('[data-bunaroba-free-shipping]')?.dataset;
    if (!data?.giftState) return null;
    return { state: data.giftState, base: Number(data.giftBase), threshold: Number(data.giftThreshold), remaining: Number(data.giftRemaining) };
  }

  const zurichDate = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date());

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  class CartState extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        try {
          currentState = JSON.parse(this.querySelector('script').textContent);
          document.dispatchEvent(new CustomEvent(stateEvent, { detail: currentState }));
        } catch { /* Der native Warenkorb bleibt auch ohne Zusatzanzeige bedienbar. */ }
      });
    }
  }

  class Recommendations extends HTMLElement {
    connectedCallback() {
      if (this.relocating) return;
      this.controller = new AbortController();
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.results = this.querySelector('[data-cart-suggestions]');
      this.feedback = this.querySelector('[data-cart-feedback]');
      document.addEventListener(stateEvent, event => this.refresh(event.detail), { signal: this.controller.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-add-variant]');
        if (button) this.add(button);
      }, { signal: this.controller.signal });
      if (currentState) this.refresh(currentState);
    }

    disconnectedCallback() {
      if (this.relocating) return;
      this.controller.abort();
      this.generation = (this.generation || 0) + 1;
    }

    async refresh(state) {
      this.state = state;
      const generation = this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      this.results.replaceChildren();
      if (!eligible(state)) return;
      const ids = [...new Set(state.products)];
      const supported = state.currency === 'CHF' && ['CH', 'LI'].includes(state.country);
      const repeat = state.repeatCreatine;
      const repeatProduct = repeat?.product;
      const repeatVariant = repeatProduct?.variants?.length === 1 && repeatProduct.variants[0];
      // Der Zusatz verwendet genau die vorhandene Variante. Shopify prüft Preis und Bestand beim Hinzufügen.
      const repeatEligible = supported && !state.discount && state.count === 1 && ids.length === 1
        && repeat?.quantity === 1 && String(ids[0]) === String(repeatProduct?.id)
        && repeatProduct?.available && !repeatProduct.requires_selling_plan && repeatVariant?.available
        && repeatVariant.price > 0 && state.total < 5000 && state.total + repeatVariant.price >= 5000;
      if (repeatEligible) {
        const group = element('section');
        group.append(element('h2', '', this.labels.related));
        // Bis 08.11.2026 gilt bei zwei Kreatin die «Limited Offer» statt des Geschenks.
        const offer = gift();
        const withGift = offer?.state === 'locked' && zurichDate() >= '2026-11-09'
          && offer.base + repeatVariant.price >= offer.threshold;
        const repeatCard = this.card(repeatProduct, withGift ? this.labels.repeatShippingGift : this.labels.repeatShipping);
        repeatCard.dataset.suggestionKind = 'repeat';
        group.append(repeatCard);
        const protein = (state.curated?.[String(ids[0])] || []).find(product =>
          product?.available && product.variants?.some(variant => variant.available)
          && ['bunaroba-proteinpulver-nature', 'bunaroba-proteinpulver-vanille'].includes(product.handle));
        if (protein) {
          const proteinCard = this.card(protein);
          proteinCard.dataset.suggestionKind = 'related';
          group.append(proteinCard);
        }
        this.results.append(group);
        // Dieser Vorschlag braucht den Empfehlungsdienst nicht und erscheint sofort.
        this.finish(state);
        return;
      }
      const perProduct = await Promise.all(ids.map(async id => [
        ...(state.curated?.[String(id)] || []),
        ...await recommendation(id, 'complementary'),
        ...await recommendation(id, 'related')
      ]));
      if (!this.isConnected || generation !== this.generation) return;
      const seen = new Set(ids.map(String));
      const products = [];
      // Abwechselnd je Warenkorbprodukt, damit nicht nur der erste Artikel zählt.
      const length = Math.max(0, ...perProduct.map(list => list.length));
      for (let index = 0; index < length; index++) {
        for (const list of perProduct) {
          const product = list[index];
          if (!product || seen.has(String(product.id))) continue;
          seen.add(String(product.id));
          if (!product.available) continue;
          products.push(product);
        }
      }
      const remaining = Math.max(0, 5000 - state.total);
      // Nach dem Gratisversand ein Vorschlag bis zum Geschenk. Die Schwelle zählt vor Bestellrabatten,
      // darum auch bei Treuerabatt. Erst ab CHF 19 bleibt nach Abzug des Glases Marge (Chia, Kakao nie).
      const offer = gift();
      const giftGap = supported && !remaining && ['locked', 'paid'].includes(offer?.state) ? offer.remaining : 0;
      // Die Reihenfolge berücksichtigt passende Ergänzungen, nicht nur den niedrigsten Preis.
      // Ein Rabatt kann auch den Zusatzartikel betreffen. Ohne genaue Berechnung kein Gratisversand-Versprechen.
      const filler = giftGap || (supported && !state.discount && remaining > 0) ? products.find(product => {
        const variant = product.variants?.length === 1 && product.variants[0];
        if (!variant?.available || product.requires_selling_plan) return false;
        return giftGap ? variant.price >= Math.max(giftGap, 1900) : variant.price >= remaining && variant.price <= 2500;
      }) : null;
      const related = products.filter(product => product !== filler).slice(0, filler ? 1 : 2);
      if (related.length) this.group(this.labels.related, related, 'related');
      if (filler) this.group(giftGap ? this.labels.giftFiller : this.labels.filler, [filler], giftGap ? 'gift' : 'filler');
      this.finish(state);
    }

    finish(state) {
      this.hidden = !this.results.childElementCount;
      this.dataset.total = String(state.total);
      if (this.restoreCheckoutFocus) {
        this.restoreCheckoutFocus = false;
        const container = this.closest('header-cart-drawer') || document;
        const checkout = container.querySelector('[name="checkout"]');
        checkout?.focus({ preventScroll: true });
        if (!this.closest('header-cart-drawer')) checkout?.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }

    group(title, products, kind, note) {
      const group = element('section');
      group.dataset.suggestionKind = kind;
      group.append(element('h2', '', title));
      for (const product of products) group.append(this.card(product, note));
      this.results.append(group);
    }

    card(product, note) {
      const card = element('div', 'bunaroba-cart-suggestion');
      card.dataset.productId = String(product.id);
      const href = new URL(product.url || `${root()}products/${product.handle}`, location.origin);
      // Shopify liefert die Empfehlungsparameter mit. Für Auswertungen erhalten.
      if (href.origin !== location.origin) href.href = `${location.origin}${root()}products/${product.handle}`;
      const imageLink = element('a');
      imageLink.href = href.href;
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      const source = product.featured_image || product.images?.[0];
      if (source) {
        const image = element('img');
        const url = new URL(typeof source === 'string' ? source : source.url, location.origin);
        url.searchParams.set('width', '160');
        image.src = url.href;
        image.alt = '';
        image.width = 64;
        image.height = 72;
        image.loading = 'lazy';
        imageLink.append(image);
      }
      const info = element('div');
      const title = element('a', 'bunaroba-cart-suggestion__name', product.title);
      title.href = href.href;
      const variants = product.variants || [];
      const single = variants.length === 1 && variants[0].available && !product.requires_selling_plan;
      const price = single ? variants[0].price : product.price;
      card.dataset.price = String(price);
      info.append(title, element('span', 'bunaroba-cart-suggestion__price', new Intl.NumberFormat(locale(), {
        style: 'currency', currency: this.state.currency
      }).format(price / 100)));
      if (note) info.append(element('span', 'bunaroba-cart-suggestion__note', note));
      const action = element(single ? 'button' : 'a', 'bunaroba-cart-suggestion__action', single ? this.labels.add : this.labels.choose);
      action.setAttribute('aria-label', `${single ? this.labels.add : this.labels.choose}: ${product.title}`);
      if (single) {
        action.type = 'button';
        action.dataset.addVariant = String(variants[0].id);
      } else action.href = href.href;
      card.append(imageLink, info, action);
      return card;
    }

    async add(button) {
      if (this.busy || button.disabled) return;
      this.busy = true;
      const restoreFocus = document.activeElement === button;
      for (const action of document.querySelectorAll('[data-add-variant]')) action.disabled = true;
      button.textContent = this.labels.adding;
      this.feedback.textContent = '';
      let failure = this.labels.error;
      let added = false;
      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.addVariant), quantity: 1 }], sections: ['cart-ajax'] })
        });
        const data = await response.json();
        if (!response.ok) { failure = data.description || failure; throw new Error(failure); }
        this.feedback.textContent = this.labels.added;
        added = true;
        this.restoreCheckoutFocus = restoreFocus;
        // Derselbe Vertrag wie Expanse Quick Add, ohne erneutes Öffnen des Warenkorbs.
        this.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          bubbles: true, detail: { product: data, addToCartBtn: button, preventCartOpen: true }
        }));
      } catch {
        this.feedback.textContent = failure;
      } finally {
        this.busy = false;
        for (const action of document.querySelectorAll('[data-add-variant]')) action.disabled = false;
        button.textContent = this.labels.add;
        if (!added && restoreFocus && button.isConnected) button.focus({ preventScroll: true });
      }
    }
  }

  class Delivery extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('script').textContent);
      this.controller = new AbortController();
      this.update = () => {
        this.render();
        clearTimeout(this.timer);
        this.timer = setTimeout(this.update, 60000 - Date.now() % 60000);
      };
      document.addEventListener(stateEvent, this.update, { signal: this.controller.signal });
      document.addEventListener('visibilitychange', this.update, { signal: this.controller.signal });
      this.update();
    }

    disconnectedCallback() {
      this.controller.abort();
      clearTimeout(this.timer);
    }

    estimate(now = new Date()) {
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
      }).formatToParts(now).map(part => [part.type, part.value]));
      const iso = `${parts.year}-${parts.month}-${parts.day}`;
      if (iso < '2026-01-01' || iso > this.config.validThrough) return null;
      const today = new Date(`${iso}T12:00:00Z`);
      const isoOf = date => date.toISOString().slice(0, 10);
      const business = date => ![0, 6].includes(date.getUTCDay()) && !this.config.holidays.includes(isoOf(date));
      const tomorrow = date => { const next = new Date(date); next.setUTCDate(next.getUTCDate() + 1); return next; };
      const nextBusiness = date => { let next = tomorrow(date); while (!business(next)) next = tomorrow(next); return next; };
      const beforeCutoff = business(today) && Number(parts.hour) < (this.config.cutoffHour ?? 17);
      const ship = beforeCutoff ? today : nextBusiness(today);
      const delivery = nextBusiness(ship);
      if (isoOf(delivery) > this.config.validThrough) return null;
      return { beforeCutoff, delivery, isTomorrow: isoOf(delivery) === isoOf(tomorrow(today)) };
    }

    render() {
      this.hidden = currentState ? !eligible(currentState) : false;
      const estimate = this.estimate();
      const message = this.querySelector('[data-delivery-message]');
      if (!estimate) { message.textContent = this.config.fallback; return; }
      const weekday = new Intl.DateTimeFormat(locale(), { weekday: 'long', timeZone: 'Europe/Zurich' }).format(estimate.delivery);
      const date = new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'long', timeZone: 'Europe/Zurich' }).format(estimate.delivery);
      const template = estimate.isTomorrow ? this.config.tomorrow : this.config.date;
      const fragments = template.split(/(\{day\}|\{weekday\}[^{}]*\{date\}|\{date\}|PostPac Priority\.?)/);
      // Datumsangabe und Versandart bleiben beim Umbruch jeweils zusammen.
      // Übersetzungen werden als Text eingesetzt, ohne HTML auszuwerten.
      message.replaceChildren(...fragments.filter(Boolean).flatMap(fragment => {
        if (fragment.includes('{')) {
          return element('strong', 'bunaroba-cart-delivery__date', fragment
            .replace('{day}', this.config.tomorrowDay)
            .replace('{weekday}', weekday).replace('{date}', date));
        }
        if (fragment.startsWith('PostPac Priority')) {
          return element('span', 'bunaroba-cart-delivery__carrier', fragment);
        }
        const label = this.config.deliveryLabel;
        const labelIndex = label ? fragment.indexOf(label) : -1;
        if (labelIndex >= 0) {
          return [
            document.createTextNode(fragment.slice(0, labelIndex)),
            element('strong', 'bunaroba-cart-delivery__label', label),
            document.createTextNode(fragment.slice(labelIndex + label.length))
          ];
        }
        return document.createTextNode(fragment);
      }));
      if (estimate.beforeCutoff) {
        message.append(element('span', 'bunaroba-cart-delivery__cutoff', this.config.cutoff));
      }
    }
  }

  class AnnouncementDelivery extends Delivery {
    render() {
      const labels = this.config.labels[document.documentElement.lang.split('-')[0]] || this.config.labels.en;
      const message = this.querySelector('[data-delivery-message]');
      const estimate = this.estimate();
      if (!estimate) { message.textContent = labels.fallback; return; }
      const weekday = new Intl.DateTimeFormat(locale(), { weekday: 'long', timeZone: 'Europe/Zurich' }).format(estimate.delivery);
      // Die Vorlagen stammen aus den vier Website-Übersetzungen. Der Wochentag
      // kommt von Intl, ohne Eingaben oder Daten aus dem Warenkorb.
      const template = estimate.isTomorrow ? labels.headerDeliveryTomorrow
        : labels.headerDelivery.replace('{weekday}', `<span class='font-medium'>${weekday}</span>`);
      message.innerHTML = template.replaceAll('font-medium', 'bunaroba-announcement-medium')
        + (estimate.beforeCutoff ? `<span class="bunaroba-announcement-cutoff"><span class="bunaroba-announcement-desktop"> ${labels.headerCutoffSuffix.trim()}</span><span class="bunaroba-announcement-mobile">${labels.headerCutoffMobile}</span></span>` : '');
    }
  }

  customElements.define('bunaroba-cart-recommendations', Recommendations);
  customElements.define('bunaroba-cart-delivery', Delivery);
  customElements.define('bunaroba-announcement-delivery', AnnouncementDelivery);
  customElements.define('bunaroba-cart-state', CartState);

  // Gratis-Kokosöl aus der Geschenk-Zeile: derselbe Weg wie die Empfehlungen.
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-bunaroba-gift-variant]');
    if (!button || button.disabled) return;
    const message = button.closest('[data-bunaroba-free-shipping]').querySelector('[role="status"]');
    const label = button.textContent;
    // Expanse ersetzt den Knopf beim Ereignis sofort. Formular und Fokus vorher merken.
    const checkout = button.closest('form')?.querySelector('[name="checkout"]');
    const restoreFocus = document.activeElement === button;
    let failure = button.dataset.error;
    button.disabled = true;
    button.textContent = button.dataset.adding;
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.bunarobaGiftVariant), quantity: 1 }], sections: ['cart-ajax'] })
      });
      const data = await response.json();
      if (!response.ok) { failure = data.description || failure; throw new Error(failure); }
      // Expanse ersetzt die Anzeige. Bis dahin bleibt der Knopf gesperrt, damit kein zweites Glas dazukommt.
      button.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        bubbles: true, detail: { product: data, addToCartBtn: button, preventCartOpen: true }
      }));
      if (restoreFocus) checkout?.focus({ preventScroll: true });
    } catch {
      message.textContent = failure;
      button.disabled = false;
      button.textContent = label;
      if (restoreFocus) button.focus({ preventScroll: true });
    }
  });

  // Der Browser stellt beim Zurückgehen auch den alten Ladezustand des Themes wieder her.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.cart__checkout[name="checkout"]').forEach(button => {
      button.classList.remove('btn--loading');
    });
  });

  // Beim Wechsel auf Mobile einen offenen Desktop-Warenkorb samt Fokus-Sperre schliessen.
  const desktop = matchMedia('(min-width: 769px)');
  desktop.addEventListener('change', event => {
    if (event.matches) return;
    const drawer = document.querySelector('header-drawer[open="cart:open"]');
    if (drawer?.isOpen) drawer.close(undefined, true);
  });

  // Der Cookie-Banner darf auf schmalen Desktop-Ansichten die feste Kasse nicht verdecken.
  const drawer = document.querySelector('header-drawer[open="cart:open"]');
  if (drawer) {
    const wide = matchMedia('(min-width: 1024px)');
    const placeSuggestions = () => {
      // Expanse lädt den Inhalt erst beim Öffnen nach. Immer die aktuelle Instanz suchen.
      const cart = drawer.querySelector('header-cart-drawer');
      const slot = cart?.querySelector('[data-bunaroba-recommendations-slot]');
      const suggestions = cart?.querySelector('bunaroba-cart-recommendations');
      if (!slot || !suggestions) return;
      const parent = wide.matches ? cart : slot;
      if (suggestions.parentElement === parent) return;
      const active = suggestions.contains(document.activeElement) && document.activeElement;
      // Das Umhängen startet keine neue Abfrage und ersetzt keine gerade bedienten Knöpfe.
      suggestions.relocating = true;
      try {
        parent.insertBefore(suggestions, wide.matches ? cart.querySelector('form') : null);
      } finally {
        suggestions.relocating = false;
      }
      if (active?.isConnected) active.focus({ preventScroll: true });
    };
    wide.addEventListener('change', placeSuggestions);
    document.addEventListener(stateEvent, placeSuggestions);
    document.addEventListener('headerDrawer:opened', placeSuggestions);
    placeSuggestions();
    let pending = false;
    const observed = new WeakSet();
    const updateSpace = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        if (!drawer.getClientRects().length) return;
        const rect = drawer.getBoundingClientRect();
        const styles = getComputedStyle(drawer);
        const inset = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
          .reduce((sum, property) => sum + (parseFloat(styles[property]) || 0), 0);
        let bottom = window.innerHeight - 12;
        const banner = document.querySelector('#shopify-pc__banner');
        if (banner) {
          if (!observed.has(banner)) { observer.observe(banner); observed.add(banner); }
          const bounds = banner.getBoundingClientRect();
          if (bounds.height && bounds.left < rect.right && bounds.right > rect.left && bounds.top > rect.top) {
            bottom = Math.min(bottom, bounds.top - 12);
          }
        }
        drawer.style.setProperty('--bunaroba-cart-height', `${Math.max(0, Math.floor(bottom - rect.top - inset))}px`);
      });
    };
    const observer = new ResizeObserver(updateSpace);
    observer.observe(drawer);
    drawer.addEventListener('transitionend', updateSpace);
    const mutations = new MutationObserver(updateSpace);
    mutations.observe(document.body, { childList: true });
    document.addEventListener('headerDrawer:opened', updateSpace);
    document.addEventListener('visitorConsentCollected', updateSpace);
    window.addEventListener('resize', updateSpace);
  }
})();
